/**
 * admin/quizzes/new.tsx — Admin create library quiz (Phase F.2).
 *
 * Form state: {title, description, sdGrade, questions}. On submit,
 * POST /api/quiz (handleAdminCreateQuiz from Phase C.1) → navigate
 * to /admin/quizzes on success.
 *
 * Validates client-side before submit (mirrors QuizInputSchema in
 * apps/portal/src/lib/quiz/validators.ts): non-empty title, sdGrade
 * 1-6, ≥1 question, no empty prompts, options.length >= 2 for
 * choice questions, correctAnswer within options range.
 */

import { type ReactNode, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import {
  QuestionEditor,
  type QuestionDraft,
  type QuestionType,
} from '../../../components/admin/QuestionEditor.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';

export const Route = createFileRoute('/admin/quizzes/new')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminQuizNewPage,
});

function emptyQuestion(type: QuestionType = 'single_choice'): QuestionDraft {
  return {
    type,
    difficulty: 'easy',
    prompt: '',
    options: type === 'true_false' ? null : ['Pilihan A', 'Pilihan B'],
    correctAnswer: type === 'true_false' ? 'true' : '0',
  };
}

function validate(input: { title: string; sdGrade: number; questions: QuestionDraft[] }): string | null {
  if (!input.title.trim()) return 'Judul quiz wajib diisi.';
  if (input.sdGrade < 1 || input.sdGrade > 6) return 'Tingkat harus 1-6.';
  if (input.questions.length === 0) return 'Quiz harus punya minimal 1 soal.';
  for (let i = 0; i < input.questions.length; i++) {
    const q = input.questions[i]!;
    if (!q.prompt.trim()) return `Soal ${i + 1}: pertanyaan wajib diisi.`;
    if (q.type === 'single_choice' || q.type === 'multi_select') {
      const opts = q.options ?? [];
      if (opts.length < 2) return `Soal ${i + 1}: minimal 2 pilihan.`;
      if (opts.some((o) => !o.trim())) return `Soal ${i + 1}: semua pilihan harus diisi.`;
    }
    if (q.type === 'single_choice') {
      const idx = Number(q.correctAnswer);
      if (!Number.isInteger(idx) || idx < 0 || idx >= (q.options?.length ?? 0)) {
        return `Soal ${i + 1}: jawaban benar (pilihan ganda) belum valid.`;
      }
    }
    if (q.type === 'multi_select') {
      const idxs = q.correctAnswer.split(',').filter(Boolean).map(Number);
      if (idxs.length < 2) return `Soal ${i + 1}: minimal 2 jawaban benar.`;
      const optsLen = q.options?.length ?? 0;
      if (idxs.some((n) => !Number.isInteger(n) || n < 0 || n >= optsLen)) {
        return `Soal ${i + 1}: jawaban benar (multi-select) belum valid.`;
      }
    }
  }
  return null;
}

function AdminQuizNewPage(): ReactNode {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sdGrade, setSdGrade] = useState(3);
  const [questions, setQuestions] = useState<QuestionDraft[]>([emptyQuestion()]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateQuestion = (i: number, q: QuestionDraft): void => {
    setQuestions((prev) => prev.map((old, idx) => (idx === i ? q : old)));
  };
  const removeQuestion = (i: number): void => {
    setQuestions((prev) => prev.filter((_, idx) => idx !== i));
  };
  const addQuestion = (): void => {
    setQuestions((prev) => [...prev, emptyQuestion()]);
  };

  const onSubmit = async (): Promise<void> => {
    setError(null);
    const validationError = validate({ title, sdGrade, questions });
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch('/api/quiz/admin', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || undefined,
          sdGrade,
          questions: questions.map((q) => ({
            type: q.type,
            difficulty: q.difficulty,
            prompt: q.prompt.trim(),
            options: q.options,
            correctAnswer: q.correctAnswer,
          })),
        }),
      });
      const j = (await r.json()) as { ok: boolean; data?: { quizId: string }; error?: { message: string } };
      if (!j.ok || !j.data) {
        setError(j.error?.message ?? 'Gagal membuat quiz.');
        setSubmitting(false);
        return;
      }
      await navigate({ to: '/admin/quizzes' });
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Kelola Quiz"
        title="Buat Quiz Baru"
        description="Quiz baru otomatis masuk Library (terlihat untuk semua tutor di tingkat yang sama)."
        action={
          <button
            type="button"
            className="button button-primary"
            onClick={onSubmit}
            disabled={submitting}
            data-testid="quiz-submit"
          >
            {submitting ? 'Menyimpan…' : 'Simpan'}
          </button>
        }
      />

      {error ? (
        <div className="form-message is-error" role="alert" data-testid="quiz-error">
          {error}
        </div>
      ) : null}

      <div className="form-grid">
        <label className="form-field">
          <span className="form-label">Judul</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Contoh: Pola & Deret Bilangan"
            data-testid="quiz-title"
          />
        </label>
        <label className="form-field">
          <span className="form-label">Tingkat</span>
          <select
            value={sdGrade}
            onChange={(e) => setSdGrade(Number(e.target.value))}
            data-testid="quiz-grade"
          >
            {[1, 2, 3, 4, 5, 6].map((g) => (
              <option key={g} value={g}>
                Kelas {g}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="form-field">
        <span className="form-label">Deskripsi (opsional)</span>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Satu kalimat tentang isi quiz"
        />
      </label>

      <section className="section">
        <h2 className="section-title">Soal ({questions.length})</h2>
        <div className="stack">
          {questions.map((q, i) => (
            <QuestionEditor
              key={i}
              index={i}
              value={q}
              onChange={(next) => updateQuestion(i, next)}
              onRemove={() => removeQuestion(i)}
            />
          ))}
        </div>
        <div className="cluster" style={{ marginTop: 'var(--spacing-3)' }}>
          <button
            type="button"
            className="button button-secondary"
            onClick={addQuestion}
            data-testid="quiz-add-question"
          >
            + Tambah Soal
          </button>
          {questions.length === 0 ? (
            <StatePanel
              variant="empty"
              title="Belum ada soal"
              description="Klik 'Tambah Soal' untuk membuat soal pertama."
            />
          ) : null}
        </div>
      </section>
    </main>
  );
}
